// Pure quote/pricing helpers, shared between the app calculators (tour, package,
// transfer) and usable from the Nitro server. Everything here is framework-free on
// purpose — the same rules power the tour price calculator, the package booking
// widget and the transfer calculator, so the three can never drift.
//
// The shapes these helpers read are Storyblok blok rows, but they only touch the
// fields listed per function; callers pre-filter rows (by category + guest count on
// tours, by hotel tier on packages, by route on transfers) and read the price field
// they own (`price` on tours, `price_adult`/`price_child` on packages).

// Storyblok number fields arrive as '' when unset, not missing — treat every empty
// form as the fallback, anything else as a number.
export function bound(value, fallback) {
    return value === '' || value === null || value === undefined ? fallback : Number(value)
}

// Three-state season match for one row against a travel date:
//   null  — the row has neither season bound, so it is the unconditional all-year
//           fallback and must not beat a genuinely in-season row
//   false — the row is seasonal and the date is missing or outside the window
//   true  — the row is seasonal and the date falls inside the (inclusive) window
export function isRowInSeason(row, travelDate) {
    if (!row.season_start && !row.season_end)
        return null
    if (!travelDate)
        return false
    const d = new Date(travelDate)
    if (row.season_start && d < new Date(row.season_start))
        return false
    if (row.season_end && d > new Date(row.season_end))
        return false
    return true
}

// Picks the row to price with: the first genuinely in-season row wins; otherwise the
// first all-year fallback row; null when neither exists (callers show a
// "contact us for a quote" note). Pass rows already filtered to the dimension you
// price on (guest-count band, hotel tier, route…).
export function resolveSeasonalRow(rows, travelDate) {
    const seasonal = rows.find(row => isRowInSeason(row, travelDate) === true)
    if (seasonal)
        return seasonal
    return rows.find(row => !row.season_start && !row.season_end) || null
}

// Cheapest row whose capacity fits the passenger count. Transfer rows carry
// `max_passengers` + `price`; used by the transfer calculator, where "no row fits"
// likewise means a manual quote.
export function resolveCapacityRow(rows, passengers) {
    let best = null
    for (const row of rows) {
        if (bound(row.max_passengers, 0) < passengers)
            continue
        if (best === null || Number(row.price) < Number(best.price))
            best = row
    }
    return best
}

// Breakdown lines are `{ label, amount }` everywhere; total is always their sum.
export function sumBreakdown(lines) {
    return lines.reduce((sum, line) => sum + line.amount, 0)
}
